import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { portalApi } from '../services/portalApi';
import { queryKeys } from './queryKeys';
import { useApiMutation } from '../hooks/useApiMutation';

const merge = (owner, values) => ({ ...owner, ...values, lead: values.lead ? { ...owner.lead, ...values.lead } : owner.lead });
const patchOwner = (id, values) => (o) => (o._id === id ? merge(o, values) : o);

const useOwnerMutation = (opts) =>
  useApiMutation({ invalidate: [queryKeys.owners.all, queryKeys.properties.all, queryKeys.contacts.all], ...opts });

function useOwnerList(params) {
  return useQuery({
    queryKey: queryKeys.owners.list(params),
    queryFn: () => portalApi.owners(params),
    placeholderData: keepPreviousData
  });
}

export function useOwners({ page = 1, pageSize = 10, search = '' } = {}) {
  const query = useOwnerList({ page, limit: pageSize, sort: 'name', search: search || undefined });
  return {
    rows: query.data?.docs || [],
    total: query.data?.totalDocs || 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error
  };
}

export function useOwnerOptions() {
  const query = useOwnerList({ limit: 500, sort: 'name' });
  return (query.data?.docs || []).map((o) => ({ value: o._id, label: o.name }));
}

export function useOwnerLeads() {
  const query = useOwnerList({ status: 'lead', limit: 100, sort: '-lead.score' });
  return { leads: query.data?.docs || [], isLoading: query.isLoading, error: query.error };
}

export function useOwnerQuery(id) {
  return useQuery({
    queryKey: queryKeys.owners.detail(id),
    queryFn: () => portalApi.owner(id),
    enabled: Boolean(id)
  });
}

export function useCreateOwner() {
  return useOwnerMutation({
    mutationFn: (values) => portalApi.createOwner(values),
    success: (data) => `${data.owner.name} added to owners`,
    error: 'Could not add this owner'
  });
}

export function useUploadOwnerFiles() {
  return useOwnerMutation({
    mutationFn: ({ id, files }) => Promise.all(files.map((f) => portalApi.uploadOwnerFile(id, f.file, f.type))),
    success: (data) => (data.length === 1 ? 'File uploaded' : `${data.length} files uploaded`),
    error: 'Could not upload this file'
  });
}

export function useDeleteOwnerFile() {
  return useOwnerMutation({
    mutationFn: ({ id, fileId }) => portalApi.deleteOwnerFile(id, fileId),
    success: 'File removed',
    error: 'Could not remove this file'
  });
}

export function useDeleteOwner() {
  return useOwnerMutation({
    mutationFn: (id) => portalApi.removeOwner(id),
    success: 'Owner removed',
    error: 'Could not remove this owner'
  });
}

export function useUpdateOwner({ success = 'Owner updated' } = {}) {
  return useOwnerMutation({
    mutationFn: ({ _id, ...values }) => portalApi.updateOwner(_id, values),
    optimistic: ({ _id, listingIds, ...values }) => [
      { key: queryKeys.owners.lists(), update: (data) => ({ ...data, docs: data.docs.map(patchOwner(_id, values)) }) },
      { key: queryKeys.owners.detail(_id), update: (data) => ({ ...data, owner: merge(data.owner, values) }) }
    ],
    success,
    error: 'Could not update this owner'
  });
}
